import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ReferenceDot, CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { authAxios } from "../utils/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Diamond, Plus, RefreshCw, ChevronLeft, ChevronRight, Trash2, Users, X,
} from "lucide-react";
import { toast } from "sonner";

const GRANULARITIES = [
  { value: "day",   label: "Day" },
  { value: "week",  label: "Week" },
  { value: "month", label: "Month" },
  { value: "year",  label: "Year" },
];

// Activity filter — switches which series is charted (dataKey + color).
const ACTIVITY_MODES = [
  { value: "all",      label: "All",      dataKey: "total",    color: "#d4af6a" },
  { value: "active",   label: "Active",   dataKey: "active",   color: "#34d399" },
  { value: "inactive", label: "Inactive", dataKey: "inactive", color: "#94a3b8" },
];

// Convert a bucket string (2026-02-14, 2026-W07, 2026-02, 2026) to sortable numeric ts
function bucketToTs(b, gran) {
  if (!b) return 0;
  if (gran === "day")   return new Date(b + "T00:00:00Z").getTime();
  if (gran === "month") return new Date(b + "-01T00:00:00Z").getTime();
  if (gran === "year")  return new Date(b + "-01-01T00:00:00Z").getTime();
  if (gran === "week") {
    const [y, w] = b.split("-W");
    // ISO week: Jan 4 always in week 1
    const jan4 = new Date(Date.UTC(Number(y), 0, 4));
    const start = jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * 86400000
                + (Number(w) - 1) * 7 * 86400000;
    return start;
  }
  return 0;
}

// Which bucket does a date fall into for a given granularity?
function dateToBucket(iso, gran) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  if (gran === "day")   return `${y}-${m}-${day}`;
  if (gran === "month") return `${y}-${m}`;
  if (gran === "year")  return `${y}`;
  if (gran === "week") {
    // ISO week number
    const target = new Date(Date.UTC(y, d.getUTCMonth(), d.getUTCDate()));
    const dayNum = (target.getUTCDay() + 6) % 7;
    target.setUTCDate(target.getUTCDate() - dayNum + 3);
    const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round(((target - firstThursday) / 86400000 - 3
                                 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
    return `${target.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
  }
  return null;
}

// Convert an ISO string to a value usable in <input type="datetime-local"> (local TZ)
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert the datetime-local input back to a UTC ISO string
function fromLocalInput(local) {
  if (!local) return null;
  const d = new Date(local); // local time
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

const DiamondMarker = ({ cx, cy, marker, onOpen }) => (
  <g
    transform={`translate(${cx},${cy - 22})`}
    style={{ cursor: "pointer" }}
    onClick={() => onOpen(marker)}
    data-testid={`timeline-event-marker-${marker.event_id}`}
  >
    <title>{marker.title}</title>
    <polygon
      points="0,-8 8,0 0,8 -8,0"
      fill="#7dd3fc"
      stroke="#0a0a0f"
      strokeWidth={1.5}
    />
    <line x1={0} y1={8} x2={0} y2={20} stroke="#7dd3fc" strokeWidth={1} strokeDasharray="2 2" opacity={0.5} />
  </g>
);

const AffiliateDownlineTimeline = () => {
  const [granularity, setGranularity] = useState("month");
  const [activityMode, setActivityMode] = useState("all"); // all | active | inactive
  const [data, setData] = useState([]);           // [{bucket, tier_1, tier_2, tier_3, total, active, inactive, first_signup_at}]
  const [events, setEvents] = useState([]);       // user-created timeline events
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0); // buckets from the right edge
  const [selectedBucket, setSelectedBucket] = useState(null); // for anchoring granularity + Add Event
  const scrollRef = useRef(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);   // if set, delete button appears
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formDateTime, setFormDateTime] = useState("");     // yyyy-MM-ddTHH:mm
  const [saving, setSaving] = useState(false);

  // Drilldown modal state — opens on datapoint click, lists users who signed
  // up inside that bucket (with each user's own direct-affiliates count).
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillBucket, setDrillBucket] = useState(null);
  const [drillUsers, setDrillUsers] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrilldown = useCallback(async (bucket) => {
    if (!bucket) return;
    setSelectedBucket(bucket);
    setDrillBucket(bucket);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillUsers([]);
    try {
      const res = await authAxios.get(
        `/affiliate/downline-signups/detail?bucket=${encodeURIComponent(bucket)}&granularity=${granularity}&tier=all`
      );
      setDrillUsers(res?.data?.users || []);
    } catch (err) {
      toast.error("Failed to load signup details");
    } finally {
      setDrillLoading(false);
    }
  }, [granularity]);

  // Sync with payouts panel range dropdown (via window event bus).
  // Range → auto-select granularity + visible-window size.
  useEffect(() => {
    const handler = (e) => {
      const days = e?.detail?.rangeDays;
      if (!days) return;
      const [gran, buckets] =
        days <= 7   ? ["day",   7]   :
        days <= 30  ? ["day",   30]  :
        days <= 90  ? ["week",  Math.ceil(90 / 7)] :
                      ["month", 12];
      setSelectedBucket(null);
      setGranularity(gran);
      setScrollOffset(0);
      window.__eros_timeline_visible = buckets;
    };
    window.addEventListener("agent-eros:payouts-range", handler);
    return () => window.removeEventListener("agent-eros:payouts-range", handler);
  }, []);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, e] = await Promise.all([
        authAxios.get(`/affiliate/downline-signups?granularity=${granularity}`),
        authAxios.get(`/affiliate/timeline-events`),
      ]);
      setData(s?.data?.data || []);
      setEvents(e?.data?.events || []);
    } catch (err) {
      console.error("timeline fetch failed:", err);
      toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [granularity]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build the full, dense bucket list from earliest signup or earliest event → now,
  // padding zero rows for empty periods so the chart shows continuity.
  const chartData = useMemo(() => {
    if (data.length === 0 && events.length === 0) return [];
    const allTs = [
      ...data.map((d) => bucketToTs(d.bucket, granularity)),
      ...events.map((ev) => new Date(ev.event_at).getTime()),
      Date.now(),
    ].filter((t) => t > 0);
    if (allTs.length === 0) return [];

    const startTs = Math.min(...allTs);
    const endTs = Date.now();
    const step = { day: 86400000, week: 604800000, month: 30 * 86400000, year: 365 * 86400000 }[granularity];

    const byBucket = Object.fromEntries(data.map((d) => [d.bucket, d]));
    const out = [];
    let cursor = startTs;
    // Cap iteration count to avoid runaway loops
    let safety = 500;
    while (cursor <= endTs + step && safety-- > 0) {
      const bucket = dateToBucket(new Date(cursor).toISOString(), granularity);
      if (!bucket) break;
      const row = byBucket[bucket] || { bucket, tier_1: 0, tier_2: 0, tier_3: 0, total: 0, active: 0, inactive: 0 };
      if (!out.find((r) => r.bucket === bucket)) out.push({ ...row, ts: bucketToTs(bucket, granularity) });
      cursor += step;
    }
    return out;
  }, [data, events, granularity]);

  // Determine visible window (last VISIBLE_BUCKETS buckets minus scrollOffset)
  const VISIBLE_BUCKETS = (typeof window !== "undefined" && window.__eros_timeline_visible) || 20;
  const visibleData = useMemo(() => {
    if (chartData.length === 0) return [];
    const end = chartData.length - scrollOffset;
    const start = Math.max(0, end - VISIBLE_BUCKETS);
    return chartData.slice(start, end);
  }, [chartData, scrollOffset]);

  // Map an event to a chart X-value (bucket ts) and Y-value (that bucket's charted series)
  const activity = ACTIVITY_MODES.find((m) => m.value === activityMode) || ACTIVITY_MODES[0];
  const eventMarkers = useMemo(() => {
    if (visibleData.length === 0) return [];
    const bucketSet = new Set(visibleData.map((d) => d.bucket));
    return events
      .map((ev) => {
        const b = dateToBucket(ev.event_at, granularity);
        if (!bucketSet.has(b)) return null;
        const row = visibleData.find((d) => d.bucket === b);
        return {
          event_id: ev.event_id,
          title: ev.title,
          description: ev.description,
          event_at: ev.event_at,
          bucket: b,
          ts: bucketToTs(b, granularity),
          y: row ? (row[activity.dataKey] || 0) : 0,
        };
      })
      .filter(Boolean);
  }, [events, visibleData, granularity, activity.dataKey]);

  // Change granularity; if a bucket is selected, anchor the new view around it.
  const changeGranularity = (nextGran) => {
    if (nextGran === granularity) return;
    // Prefer the selected bucket's first_signup_at (real data point); fall back to bucket start.
    let anchorIso = null;
    if (selectedBucket) {
      const row = chartData.find((r) => r.bucket === selectedBucket);
      anchorIso = row?.first_signup_at || new Date(bucketToTs(selectedBucket, granularity)).toISOString();
    }
    setGranularity(nextGran);
    if (!anchorIso) { setScrollOffset(0); return; }
    // Defer to the next tick so chartData recomputes with new granularity.
    setTimeout(() => {
      const anchorBucket = dateToBucket(anchorIso, nextGran);
      setSelectedBucket(anchorBucket);
    }, 0);
  };

  // Whenever chartData recomputes and a selection exists, re-align scroll offset
  // so the selected bucket is inside the visible window.
  useEffect(() => {
    if (!selectedBucket || chartData.length === 0) return;
    const idx = chartData.findIndex((r) => r.bucket === selectedBucket);
    if (idx === -1) return;
    // We want the selected bucket in the last VISIBLE_BUCKETS window: end = length - offset.
    // Compute the smallest offset that includes idx: end >= idx + 1 → offset <= length - idx - 1.
    // Position selection ~2/3 into the window for context.
    const desiredEnd = Math.min(chartData.length, idx + 6);
    const nextOffset = Math.max(0, chartData.length - desiredEnd);
    setScrollOffset(nextOffset);
  }, [chartData, selectedBucket]);

  const openAddModal = () => {
    setEditingEvent(null);
    setFormTitle("");
    setFormDesc("");
    // Prefill datetime from the selected bucket's first signup / bucket start, else now.
    let prefill = new Date().toISOString();
    if (selectedBucket) {
      const row = chartData.find((r) => r.bucket === selectedBucket);
      if (row?.first_signup_at) prefill = row.first_signup_at;
      else prefill = new Date(bucketToTs(selectedBucket, granularity)).toISOString();
    }
    setFormDateTime(toLocalInput(prefill));
    setModalOpen(true);
  };

  const openViewModal = (ev) => {
    setEditingEvent(ev);
    setFormTitle(ev.title || "");
    setFormDesc(ev.description || "");
    setFormDateTime(toLocalInput(ev.event_at));
    setModalOpen(true);
  };

  const saveEvent = async () => {
    if (!formTitle.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (editingEvent) {
        await authAxios.put(`/affiliate/timeline-events/${editingEvent.event_id}`, {
          title: formTitle, description: formDesc,
          event_at: fromLocalInput(formDateTime),
        });
        toast.success("Event updated");
      } else {
        await authAxios.post(`/affiliate/timeline-events`, {
          title: formTitle, description: formDesc,
          event_at: fromLocalInput(formDateTime),
        });
        toast.success("Event added");
      }
      setModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!editingEvent) return;
    if (!window.confirm(`Delete "${editingEvent.title}"?`)) return;
    setSaving(true);
    try {
      await authAxios.delete(`/affiliate/timeline-events/${editingEvent.event_id}`);
      toast.success("Event deleted");
      setModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  const canScrollLeft  = scrollOffset < Math.max(0, chartData.length - VISIBLE_BUCKETS);
  const canScrollRight = scrollOffset > 0;

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-6 flex items-center justify-center">
        <RefreshCw className="w-5 h-5 text-divine-gold animate-spin" />
      </div>
    );
  }

  return (
    <section data-testid="downline-timeline-section" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-divine-gold">Referral Signups</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Activity filter — All / Active / Inactive */}
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden" data-testid="activity-mode-selector">
            {ACTIVITY_MODES.map((m) => (
              <button
                key={m.value}
                onClick={() => setActivityMode(m.value)}
                data-testid={`activity-${m.value}`}
                title={
                  m.value === "active"   ? "Only signups who are Active Customers (paid tier or completed payment in the last 30 days)"
                  : m.value === "inactive" ? "Only signups who are NOT Active Customers (free tier and no recent payment)"
                  : "All signups regardless of activity"
                }
                className={`px-3 py-1.5 text-xs transition ${
                  activityMode === m.value
                    ? "bg-divine-gold text-black font-medium"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
            {GRANULARITIES.map((g) => (
              <button
                key={g.value}
                onClick={() => changeGranularity(g.value)}
                data-testid={`gran-${g.value}`}
                className={`px-3 py-1.5 text-xs transition ${
                  granularity === g.value
                    ? "bg-divine-gold text-black font-medium"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            onClick={openAddModal}
            className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium"
            data-testid="add-timeline-event-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Add event
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4 relative" ref={scrollRef}>
        {/* Scroll controls */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
          <button
            onClick={() => canScrollLeft && setScrollOffset((s) => s + 5)}
            disabled={!canScrollLeft}
            className={`p-1 rounded-full bg-black/40 border border-white/10 ${canScrollLeft ? "hover:bg-black/60" : "opacity-30 cursor-not-allowed"}`}
            data-testid="timeline-scroll-left"
            aria-label="Scroll to past"
          >
            <ChevronLeft className="w-4 h-4 text-white/70" />
          </button>
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
          <button
            onClick={() => canScrollRight && setScrollOffset((s) => Math.max(0, s - 5))}
            disabled={!canScrollRight}
            className={`p-1 rounded-full bg-black/40 border border-white/10 ${canScrollRight ? "hover:bg-black/60" : "opacity-30 cursor-not-allowed"}`}
            data-testid="timeline-scroll-right"
            aria-label="Scroll to present"
          >
            <ChevronRight className="w-4 h-4 text-white/70" />
          </button>
        </div>

        {visibleData.length === 0 ? (
          <div className="h-56 flex flex-col items-center justify-center text-white/40 text-sm" data-testid="timeline-empty">
            <p>No signups tracked yet.</p>
            <p className="text-xs mt-1">Share your affiliate link — new signups will appear here.</p>
          </div>
        ) : (
          <div className="h-56 px-8">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visibleData} margin={{ top: 12, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="bucket"
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fontSize: 10 }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{ background: "#0a0a0f", border: "1px solid rgba(212,175,106,0.3)", borderRadius: 8 }}
                  labelStyle={{ color: "#d4af6a" }}
                />
                <Line
                  type="monotone" dataKey={activity.dataKey} stroke={activity.color} strokeWidth={2}
                  activeDot={{ r: 6, fill: activity.color, cursor: "pointer",
                    onClick: (_, payload) => openDrilldown(payload?.payload?.bucket) }}
                  // eslint-disable-next-line react/no-unstable-nested-components
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    const isSel = payload?.bucket === selectedBucket;
                    const hasSignups = (payload?.[activity.dataKey] || 0) > 0;
                    return (
                      <circle
                        cx={cx} cy={cy} r={isSel ? 6 : (hasSignups ? 4 : 3)}
                        fill={isSel ? "#fff" : activity.color}
                        stroke={isSel ? activity.color : (hasSignups ? "#fff" : "none")}
                        strokeWidth={isSel ? 2 : (hasSignups ? 1 : 0)}
                        style={{ cursor: hasSignups ? "pointer" : "default" }}
                        onClick={() => hasSignups && openDrilldown(payload?.bucket)}
                        data-testid={`timeline-dot-${payload?.bucket}`}
                      />
                    );
                  }}
                  name={activity.label === "All" ? "Signups" : `${activity.label} signups`}
                />
                {/* Event diamonds */}
                {eventMarkers.map((m) => (
                  <ReferenceDot
                    key={m.event_id}
                    x={m.bucket}
                    y={m.y}
                    r={8}
                    isFront
                    // eslint-disable-next-line react/no-unstable-nested-components
                    shape={(props) => (
                      <DiamondMarker cx={props.cx} cy={props.cy} marker={m} onOpen={openViewModal} />
                    )}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between text-[10px] text-white/40 px-8">
          <span>← Past</span>
          <span className="flex items-center gap-2">
            <span className="flex items-center gap-1">
              <Diamond className="w-3 h-3 text-sky-300 fill-sky-300" /> = your logged events
            </span>
            {selectedBucket && (
              <>
                <span className="text-white/30">·</span>
                <button
                  onClick={() => setSelectedBucket(null)}
                  className="text-divine-gold hover:underline"
                  data-testid="clear-selection"
                >
                  Clear selection ({selectedBucket})
                </button>
              </>
            )}
          </span>
          <span>Present →</span>
        </div>
      </div>

      {/* Add / View / Edit Event Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="bg-[#0a0a0f] border border-divine-gold/25 text-white"
          data-testid="timeline-event-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-divine-gold flex items-center gap-2">
              <Diamond className="w-4 h-4 text-sky-300 fill-sky-300" />
              {editingEvent ? "Timeline Event" : "Add Timeline Event"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-white/50">
              Add events to your timeline to log any marketing or promotions — spot which
              campaign moved signups on the chart above.
            </p>
            <div>
              <label className="text-xs text-white/60 block mb-1">Title</label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Instagram launch post"
                className="bg-white/5 border-white/10"
                data-testid="timeline-event-title-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Date & Time</label>
              <Input
                type="datetime-local"
                value={formDateTime}
                onChange={(e) => setFormDateTime(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="timeline-event-datetime-input"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Description</label>
              <textarea
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Optional — details, links, audience, etc."
                rows={4}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="timeline-event-desc-input"
              />
            </div>
            {editingEvent && (
              <div className="text-[10px] text-white/40">
                Recorded {new Date(editingEvent.event_at).toLocaleString()}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            {editingEvent && (
              <Button
                variant="outline"
                onClick={deleteEvent}
                disabled={saving}
                className="border-red-500/40 text-red-400 hover:bg-red-500/10 mr-auto"
                data-testid="timeline-event-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setModalOpen(false)}
              className="text-white/60 hover:text-white"
              data-testid="timeline-event-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEvent}
              disabled={saving || !formTitle.trim()}
              className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium"
              data-testid="timeline-event-save-btn"
            >
              {saving ? "Saving…" : editingEvent ? "Save changes" : "Add event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drilldown: users who signed up in a bucket */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent
          className="bg-[#0a0a0f] border border-divine-gold/25 text-white max-w-lg"
          data-testid="signup-drilldown-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-divine-gold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Signups in {drillBucket || "—"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {drillLoading ? (
              <div className="py-8 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-divine-gold animate-spin" />
              </div>
            ) : drillUsers.length === 0 ? (
              <div className="py-8 text-center text-white/50 text-sm" data-testid="drilldown-empty">
                No signups in this bucket.
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto -mx-6 px-6">
                <table className="w-full text-xs">
                  <thead className="text-white/40 uppercase tracking-wider text-[10px] sticky top-0 bg-[#0a0a0f]">
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left py-2 pr-3 font-medium">Name</th>
                      <th className="text-left py-2 pr-3 font-medium">Date</th>
                      <th className="text-center py-2 pr-3 font-medium">Tier</th>
                      <th className="text-center py-2 font-medium">Sub-affiliates</th>
                    </tr>
                  </thead>
                  <tbody data-testid="drilldown-user-list">
                    {drillUsers.map((u) => (
                      <tr
                        key={u.user_id}
                        className="border-b border-white/[0.03] hover:bg-white/[0.02]"
                        data-testid={`drilldown-row-${u.user_id}`}
                      >
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-[#C5A55A]/20 text-[#C5A55A] flex items-center justify-center text-[10px] font-semibold">
                              {u.name?.[0]?.toUpperCase() || "?"}
                            </div>
                            <div>
                              <div className="text-white/85">{u.name}</div>
                              {u.is_active_customer && (
                                <div className="text-[9px] text-emerald-400/70 uppercase tracking-wider">Active</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-white/50 whitespace-nowrap">
                          {u.joined_at ? new Date(u.joined_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2 pr-3 text-center">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                            u.tier === "tier_1"
                              ? "bg-sky-500/15 text-sky-300"
                              : "bg-indigo-500/15 text-indigo-300"
                          }`}>
                            {u.tier === "tier_1" ? "Direct" : "Sub"}
                          </span>
                        </td>
                        <td className="py-2 text-center text-white/70 font-mono">
                          {u.sub_affiliates}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDrillOpen(false)}
              className="text-white/60 hover:text-white"
              data-testid="drilldown-close-btn"
            >
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default AffiliateDownlineTimeline;
